import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useProfiles, useActiveAccount, useDisconnect, useActiveWallet } from 'thirdweb/react';

import box from '../../icons/box.svg';
import home from '../../icons/home.svg';
import p_home from '../../icons/p_home.svg';
import p_box from '../../icons/p_box.svg';

import { g8wyLogo, help, sun, cross, settings, logout } from '../../assets';
import { UserMenuDropdown } from '../Dropdown';
import useOutsideClick from '../../hooks/useOutsideClick';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const activeAccount = useActiveAccount();
  const activeWallet = useActiveWallet();
  const { disconnect } = useDisconnect();
  const { data: profiles } = useProfiles();

  const [userEmail, setUserEmail] = React.useState<string>('');
  const [toggleUserMenu, setToggleUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  useOutsideClick(mobileMenuRef, () => {
    if (mobileMenuOpen) setMobileMenuOpen(false);
  });

  const handleLinkState = (state: string) => {
    setActiveTab(state);
  };

  useEffect(() => {
    if (profiles) {
      setUserEmail(profiles[0].details.email || '');
    }
  }, [profiles]);

  useEffect(() => {
    if (location.pathname === '/home') {
      setActiveTab('dashboard');
    } else if (location.pathname === '/projects') {
      setActiveTab('projects');
    } else if (location.pathname === '/contracts') {
      setActiveTab('contracts');
    } else if (location.pathname === '/financials') {
      setActiveTab('financial');
    } else if (location.pathname === '/sales') {
      setActiveTab('sales');
    }
  }, [location.pathname]);

  return (
    <nav className="w-full fixed z-50 flex justify-between items-center w-2/2 mx-auto bg-white sm:px-16 lg:px-24 px-5 shadow-[0px_4px_8px_0px_rgba(203,213,225,0.25)] py-4">
      <div className="ml-0 lg:ml-8">
        <Link to="/home" className="flex items-center gap-3">
          <img className="h-[48px] w-auto" src={g8wyLogo} alt="Equitee" />
          <span className="text-[20px] font-bold text-[#333333] font-['Plus Jakarta Sans']">Edge</span>
        </Link>
      </div>
      <div className="nav-links duration-500 w-auto hidden sm:flex items-center text-[#0F172A]">
        <ul className="flex flex-row items-center sm:gap-12 lg:gap-16 gap-3">
          <Link to="/" onClick={() => handleLinkState('dashboard')}>
            <li>
              <div className="flex hover:text-[#0F172A]">
                <img
                  src={`${activeTab === 'dashboard' ? p_home : home} `}
                  className="w-[28px] h-[28px] sm:w-[24px] sm:h-[24px] block sm:hidden"
                  alt="new_img"
                />
                <a
                  className={`hidden sm:block sm:text-[16px] sm:px-4 text-[#0F172A] transition-all duration-300 ${
                    activeTab === 'dashboard'
                      ? 'font-bold border-b-2 border-[#6741c6]'
                      : 'font-medium hover:text-[#6741c6]'
                  }`}
                  href="_#"
                >
                  Home
                </a>
              </div>
            </li>
          </Link>
          <Link to="/sales" onClick={() => handleLinkState('sales')}>
            <li>
              <div className="flex hover:text-[#0F172A]">
                <img
                  src={`${activeTab === 'sales' ? p_box : box} `}
                  className="w-[28px] h-[28px] sm:w-[24px] sm:h-[24px] block sm:hidden"
                  alt="new_img1"
                />
                <a
                  className={`hidden sm:block sm:text-[16px] sm:px-4 text-[#0F172A] transition-all duration-300 ${
                    activeTab === 'sales'
                      ? 'font-bold border-b-2 border-[#6741c6]'
                      : 'font-medium hover:text-[#6741c6]'
                  }`}
                  href="_#"
                >
                  Sales
                </a>
              </div>
            </li>
          </Link>
          <Link to="/contracts" onClick={() => handleLinkState('contracts')}>
            <li>
              <div className="flex hover:text-[#0F172A]">
                <img
                  src={`${activeTab === 'contracts' ? p_box : box} `}
                  className="w-[28px] h-[28px] sm:w-[24px] sm:h-[24px] block sm:hidden"
                  alt="new_img1"
                />
                <a
                  className={`hidden sm:block sm:text-[16px] sm:px-4 text-[#0F172A] transition-all duration-300 ${
                    activeTab === 'contracts'
                      ? 'font-bold border-b-2 border-[#6741c6]'
                      : 'font-medium hover:text-[#6741c6]'
                  }`}
                  href="_#"
                >
                  Contracts
                </a>
              </div>
            </li>
          </Link>
          <Link to="/financials" onClick={() => handleLinkState('financial')}>
            <li>
              <div className="flex hover:text-[#0F172A]">
                <img
                  src={`${activeTab === 'financial' ? p_box : box} `}
                  className="w-[28px] h-[28px] sm:w-[24px] sm:h-[24px] block sm:hidden"
                  alt="new_img1"
                />
                <a
                  className={`hidden sm:block sm:text-[16px] sm:px-4 text-[#0F172A] transition-all duration-300 ${
                    activeTab === 'financial'
                      ? 'font-bold border-b-2 border-[#6741c6]'
                      : 'font-medium hover:text-[#6741c6]'
                  }`}
                  href="_#"
                >
                  Financial
                </a>
              </div>
            </li>
          </Link>
        </ul>
      </div>
      <section className="flex flex-row gap-4 sm:gap-6 items-center">
        {!activeAccount && (
          <section className="hidden sm:block">
            <nav className="flex gap-4 items-center self-stretch my-auto text-sm font-medium leading-6 text-slate-900">
              <button
                className="btn-text"
                onClick={() => navigate('/auth/login')}
              >
                Log In
              </button>
              <div className="gradient-primary">
                <button
                  className="btn-text rounded-[5px] m-[1px] pt-[7px] px-[15px]"
                  onClick={() => navigate('/auth/signup')}
                >
                  Sign Up
                </button>
              </div>
            </nav>
          </section>
        )}
        
        {/* Desktop navigation controls */}
        <section className="hidden sm:block">
          <nav className="flex gap-2 items-center self-stretch my-auto">
            {activeAccount && (
              <section className="relative">
                <button
                  className="flex gap-2 justify-center items-center self-stretch px-3 my-auto w-10 h-10 rounded-[96px] bg-slate-200 text-base"
                  onClick={() => setToggleUserMenu(!toggleUserMenu)}
                >
                  {userEmail.length > 0
                    ? userEmail.slice(0, 2).toUpperCase()
                    : '0X'}
                </button>
                {toggleUserMenu && (
                  <UserMenuDropdown userAddress={activeAccount.address} />
                )}
              </section>
            )}
            <button
              className="flex gap-2 justify-center items-center self-stretch px-3 my-auto w-10 h-10 border border-solid border-slate-200 rounded-[96px]"
              onClick={() => window.open('https://docs.equitee.app', '_blank')}
            >
              <img
                loading="lazy"
                src={help}
                alt="help"
                className="object-contain self-stretch my-auto w-4 aspect-square"
              />
            </button>
            <button className="flex gap-2 justify-center items-center self-stretch px-3 my-auto w-10 h-10 border border-solid border-slate-200 rounded-[96px]">
              <img
                loading="lazy"
                src={sun}
                alt="sun"
                className="object-contain self-stretch my-auto w-4 aspect-square"
              />
            </button>
          </nav>
        </section>
        
        {/* Mobile menu button */}
        <button
          className="flex sm:hidden w-10 h-10 rounded-[96px] border border-solid border-slate-200 items-center justify-center"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {mobileMenuOpen ? (
            <img src={cross} alt="Close menu" className="w-4 h-4" />
          ) : (
            <div className="flex flex-col gap-1 justify-center items-center w-5 h-5">
              <div className="w-full h-[2px] bg-[#333333] rounded-full"></div>
              <div className="w-full h-[2px] bg-[#333333] rounded-full"></div>
              <div className="w-full h-[2px] bg-[#333333] rounded-full"></div>
            </div>
          )}
        </button>
      </section>
      
      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div 
          ref={mobileMenuRef}
          className="fixed top-[72px] left-0 right-0 bg-white shadow-lg z-40 flex flex-col w-full border-t border-gray-100"
        >
          <ul className="flex flex-col w-full">
            <Link to="/" onClick={() => {handleLinkState('dashboard'); setMobileMenuOpen(false);}}>
              <li className={`px-6 py-4 border-b border-gray-100 flex items-center gap-3 ${activeTab === 'dashboard' ? 'text-[#6741c6] font-medium' : 'text-[#0F172A]'}`}>
                <img
                  src={`${activeTab === 'dashboard' ? p_home : home}`}
                  className="w-5 h-5"
                  alt="home"
                />
                <span>Home</span>
              </li>
            </Link>
            <Link to="/sales" onClick={() => {handleLinkState('sales'); setMobileMenuOpen(false);}}>
              <li className={`px-6 py-4 border-b border-gray-100 flex items-center gap-3 ${activeTab === 'sales' ? 'text-[#6741c6] font-medium' : 'text-[#0F172A]'}`}>
                <img
                  src={`${activeTab === 'sales' ? p_box : box} `}
                  className="w-5 h-5"
                  alt="sales"
                />
                <span>Sales</span>
              </li>
            </Link>
            <Link to="/contracts" onClick={() => {handleLinkState('contracts'); setMobileMenuOpen(false);}}>
              <li className={`px-6 py-4 border-b border-gray-100 flex items-center gap-3 ${activeTab === 'contracts' ? 'text-[#6741c6] font-medium' : 'text-[#0F172A]'}`}>
                <img
                  src={`${activeTab === 'contracts' ? p_box : box} `}
                  className="w-5 h-5"
                  alt="contracts"
                />
                <span>Contracts</span>
              </li>
            </Link>
            <Link to="/financials" onClick={() => {handleLinkState('financial'); setMobileMenuOpen(false);}}>
              <li className={`px-6 py-4 border-b border-gray-100 flex items-center gap-3 ${activeTab === 'financial' ? 'text-[#6741c6] font-medium' : 'text-[#0F172A]'}`}>
                <img
                  src={`${activeTab === 'financial' ? p_box : box} `}
                  className="w-5 h-5"
                  alt="financial"
                />
                <span>Financial</span>
              </li>
            </Link>
            
            {activeAccount && (
              <>
                <Link to="/settings" onClick={() => {setMobileMenuOpen(false);}}>
                  <li className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                    <img
                      src={settings}
                      className="w-5 h-5"
                      alt="settings"
                    />
                    <span>Settings</span>
                  </li>
                </Link>
                <li 
                  className="px-6 py-4 border-b border-gray-100 flex items-center gap-3"
                  onClick={() => {
                    if (activeWallet) disconnect(activeWallet);
                    setMobileMenuOpen(false);
                  }}
                >
                  <img
                    src={logout}
                    className="w-5 h-5"
                    alt="logout"
                  />
                  <span>Log out</span>
                </li>
              </>
            )}
            
            {!activeAccount && (
              <>
                <Link to="/auth/login" onClick={() => setMobileMenuOpen(false)}>
                  <li className="px-6 py-4 border-b border-gray-100">Log In</li>
                </Link>
                <Link to="/auth/signup" onClick={() => setMobileMenuOpen(false)}>
                  <li className="px-6 py-4 border-b border-gray-100 text-[#6741c6] font-medium">Sign Up</li>
                </Link>
              </>
            )}
            
            <li className="px-6 py-4 flex justify-between items-center">
              <a href="https://docs.equitee.app" target="_blank" rel="noreferrer" className="flex items-center gap-3">
                <img
                  src={help}
                  className="w-5 h-5"
                  alt="help"
                />
                <span>Help</span>
              </a>
              <button className="flex gap-2 justify-center items-center px-3 my-auto w-10 h-10 border border-solid border-slate-200 rounded-[96px]">
                <img
                  src={sun}
                  alt="theme"
                  className="w-4 h-4"
                />
              </button>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
